import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  listPartners,
  createPartner,
  updatePartner,
  deletePartner,
  rotatePartnerKey, sendPartnerLogin, partnerLoginAs, listPartnerAgents,
  getPartnerLink, resetPartnerLink,
  listTenants,
} from "../lib/api";
import type { Partner, PartnerFeatureDef, Tenant } from "../types";
import type { PartnerAgentView } from "../lib/api";
import Pager, { PAGE_SIZE } from "./Pager";

/* ═══ 🤝 戰略夥伴（P1，docs/coder-brief-strategic-partners.md）═══
 * CSP「伙伴收錢＋平台歸因」：夥伴自建租戶掛 partner_id，批發價結算。
 * 與🎁推薦計劃完全獨立（D7）；API key 明文只在生成/重置一次顯示。 */

// D5 凍結白名單預設（Captain 拍板：夥伴租戶「基本運作」四項）
const DEFAULT_WHITELIST = ["core_chat", "multi_turn_memory", "smart_triage", "human_handoff"];

interface PartnerForm {
  name: string;
  website: string;
  contact_name: string;
  contact_phone: string;
  wa_line: string;
  // 批發價（HK$／計劃套餐；2026-09-13 Captain 實數——6 個月／年繳兩種）
  price_lite_6mo: string;
  price_lite_year: string;
  price_pro_6mo: string;
  price_pro_year: string;
  billing_note: string;
  // Co-Branding（P3 用；一期只做門戶＋控制台頂欄 logo＋名稱）
  co_displayName: string;
  co_logoUrl: string;
  co_portalTitle: string;
  features: string[];
}

const EMPTY_FORM: PartnerForm = {
  name: "",
  website: "",
  contact_name: "",
  contact_phone: "",
  wa_line: "",
  price_lite_6mo: "",
  price_lite_year: "",
  price_pro_6mo: "",
  price_pro_year: "",
  billing_note: "",
  co_displayName: "",
  co_logoUrl: "",
  co_portalTitle: "",
  features: [...DEFAULT_WHITELIST],
};

// 批價 key → 顯示標籤（summary card 用）
const PRICING_LABELS: Record<string, string> = {
  lite_6mo: "Lite 6個月",
  lite_year: "Lite 年繳",
  pro_6mo: "Pro 6個月",
  pro_year: "Pro 年繳",
};

function parseJsonField<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function formFromPartner(p: Partner): PartnerForm {
  const pricing = parseJsonField<Record<string, number>>(p.wholesale_pricing, {});
  const co = parseJsonField<Record<string, string>>(p.co_branding, {});
  return {
    name: p.name,
    website: p.website || "",
    contact_name: p.contact_name || "",
    contact_phone: p.contact_phone || "",
    wa_line: p.wa_line || "",
    price_lite_6mo: pricing.lite_6mo !== undefined ? String(pricing.lite_6mo) : "",
    price_lite_year: pricing.lite_year !== undefined ? String(pricing.lite_year) : "",
    price_pro_6mo: pricing.pro_6mo !== undefined ? String(pricing.pro_6mo) : "",
    price_pro_year: pricing.pro_year !== undefined ? String(pricing.pro_year) : "",
    billing_note: p.billing_note || "",
    co_displayName: co.displayName || "",
    co_logoUrl: co.logoUrl || "",
    co_portalTitle: co.portalTitle || "",
    features: parseJsonField<string[]>(p.default_features, [...DEFAULT_WHITELIST]),
  };
}

function formToPayload(f: PartnerForm) {
  const pricing: Record<string, number> = {};
  if (f.price_lite_6mo.trim() !== "") pricing.lite_6mo = Number(f.price_lite_6mo);
  if (f.price_lite_year.trim() !== "") pricing.lite_year = Number(f.price_lite_year);
  if (f.price_pro_6mo.trim() !== "") pricing.pro_6mo = Number(f.price_pro_6mo);
  if (f.price_pro_year.trim() !== "") pricing.pro_year = Number(f.price_pro_year);
  const co: Record<string, string> = {};
  if (f.co_displayName.trim()) co.displayName = f.co_displayName.trim();
  if (f.co_logoUrl.trim()) co.logoUrl = f.co_logoUrl.trim();
  if (f.co_portalTitle.trim()) co.portalTitle = f.co_portalTitle.trim();
  return {
    name: f.name,
    website: f.website || null,
    contact_name: f.contact_name || null,
    contact_phone: f.contact_phone || null,
    wa_line: f.wa_line || null,
    wholesale_pricing: Object.keys(pricing).length ? pricing : null,
    billing_note: f.billing_note || null,
    co_branding: Object.keys(co).length ? co : null,
    default_features: f.features,
  };
}

/** 明文 key 一次顯示區（生成/重置後；關閉即永不再現） */
function KeyReveal({ partnerId, apiKey, onClose }: { partnerId: string; apiKey: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mb-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/40">
      <p className="text-sm font-semibold text-emerald-400 mb-2">
        🔑 夥伴 {partnerId} 嘅 API Key（只顯示呢一次——閂咗就搵唔返，只能重新生成）
      </p>
      <div className="flex items-center gap-3">
        <code className="flex-1 block p-3 bg-slate-950 rounded-lg text-xs font-mono text-emerald-300 break-all select-all">
          {apiKey}
        </code>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(apiKey);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch { /* 手動選取都得 */ }
          }}
          className="px-4 py-2 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors shrink-0"
        >
          {copied ? "已複製 ✓" : "📋 複製"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-xs font-medium rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-100 transition-colors shrink-0"
        >
          關閉
        </button>
      </div>
    </div>
  );
}

export default function PartnersPage() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [featureDefs, setFeatureDefs] = useState<PartnerFeatureDef[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // 明文 key 一次顯示（partnerId → key）
  const [keyReveal, setKeyReveal] = useState<{ partnerId: string; apiKey: string } | null>(null);
  const [sendingLogin, setSendingLogin] = useState<string | null>(null);
  const [agentsOpen, setAgentsOpen] = useState<string | null>(null);
  const [agentsList, setAgentsList] = useState<Record<string, PartnerAgentView[]>>({});
  const [agentsLoading, setAgentsLoading] = useState<string | null>(null);
  const [copyingLink, setCopyingLink] = useState<string | null>(null);
  const [resettingLink, setResettingLink] = useState<string | null>(null);

  // 新增表單
  const [showCreate, setShowCreate] = useState(false);
  const [newId, setNewId] = useState("");
  const [createForm, setCreateForm] = useState<PartnerForm>({ ...EMPTY_FORM });
  const [creating, setCreating] = useState(false);

  // 編輯中嘅夥伴（null＝冇）
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PartnerForm>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // 每夥伴租戶清單分頁（partnerId → 0-based page）
  const [tenantPages, setTenantPages] = useState<Record<string, number>>({});
  // 展開租戶清單嘅夥伴
  const [expanded, setExpanded] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [pData, tData] = await Promise.all([listPartners(), listTenants()]);
      setPartners(pData.partners || []);
      setFeatureDefs(pData.featureDefs || []);
      setTenants(tData.tenants || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "載入失敗");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const tenantsByPartner = useMemo(() => {
    const map: Record<string, Tenant[]> = {};
    for (const t of tenants) {
      if (t.partner_id) {
        (map[t.partner_id] ||= []).push(t);
      }
    }
    return map;
  }, [tenants]);

  const toggleFeature = (form: PartnerForm, setForm: (f: PartnerForm) => void, id: string) => {
    const has = form.features.includes(id);
    setForm({
      ...form,
      features: has ? form.features.filter((x) => x !== id) : [...form.features, id],
    });
  };

  const handleCreate = async () => {
    setError("");
    const id = newId.trim().toLowerCase();
    if (!/^[a-z0-9]{2,32}$/.test(id)) {
      setError("夥伴 ID 必須係 2-32 位小寫英數（例如 eid）");
      return;
    }
    if (!createForm.name.trim()) {
      setError("夥伴名稱必填");
      return;
    }
    setCreating(true);
    try {
      const { apiKey } = await createPartner({ partner_id: id, ...formToPayload(createForm) });
      setKeyReveal({ partnerId: id, apiKey });
      setShowCreate(false);
      setNewId("");
      setCreateForm({ ...EMPTY_FORM });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "建立失敗");
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (partnerId: string) => {
    setError("");
    if (!editForm.name.trim()) {
      setError("夥伴名稱必填");
      return;
    }
    setSaving(true);
    try {
      await updatePartner(partnerId, formToPayload(editForm));
      setEditingId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "儲存失敗");
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async (p: Partner) => {
    if (!confirm(`重新生成「${p.name}」嘅 API Key？\n\n舊 Key 會即刻作廢——夥伴如果有系統用緊舊 Key 會即刻鑑權失敗，需要更新。`)) return;
    try {
      const { apiKey } = await rotatePartnerKey(p.partner_id);
      setKeyReveal({ partnerId: p.partner_id, apiKey });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "重新生成失敗");
    }
  };

  // 展開載入旗下 Agent（2026-10-30）：org 行除外；含掛靠與小代理數
  const toggleAgents = async (p: Partner) => {
    if (agentsOpen === p.partner_id) { setAgentsOpen(null); return; }
    setAgentsOpen(p.partner_id);
    if (!agentsList[p.partner_id]) {
      setAgentsLoading(p.partner_id);
      try {
        const r = await listPartnerAgents(p.partner_id);
        setAgentsList((prev) => ({ ...prev, [p.partner_id]: r.agents }));
      } catch (e) {
        setError(e instanceof Error ? e.message : "載入 Agent 失敗");
      } finally {
        setAgentsLoading(null);
      }
    }
  };

  // 代入指定 Agent 視角（login-as 帶 code——audit log 同一閘）
  const handleLoginAsAgent = async (p: Partner, a: PartnerAgentView) => {
    setSendingLogin(p.partner_id);
    try {
      const r = await partnerLoginAs(p.partner_id, a.code);
      window.open(r.url, "_blank", "noopener");
      alert(`🕵️ 已以「${r.view.name || r.view.code}」（Agent 視角）開啟夥伴後台新分頁。

此為管理員代入——已記錄審計日誌；連結 24 小時內有效。`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "代入失敗");
    } finally {
      setSendingLogin(null);
    }
  };

  // Superadmin 代入後台（2026-10-30 Captain）：不經夥伴 WhatsApp，直接以夥伴身份開後台（24h URL）
  const handleLoginAs = async (p: Partner, code?: string) => {
    setSendingLogin(p.partner_id);
    try {
      const r = await partnerLoginAs(p.partner_id, code);
      window.open(r.url, "_blank", "noopener");
      alert(`🕵️ 已以「${r.view.name || r.view.code}」（${r.view.isOrg ? "Master" : "Agent"} 視角）開啟夥伴後台新分頁。

此為管理員代入——操作會以該夥伴身份進行，已記錄審計日誌；連結 24 小時內有效。`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "代入失敗");
    } finally {
      setSendingLogin(null);
    }
  };

  // v56：手動發送登入 Magic Link（WhatsApp 直達 Master 登記電話；新作廢舊——即使未過 15 分鐘）
  // v56→2026-10-28：夥伴登入連結永久制（Captain 拍板「直接給予夥伴」）——發送仍可用，
  // 但主要交付方式＝「📋 複製連結」直接交俾夥伴；冇自動作廢（講一次關鍵字唔會殺死永久連結）。
  const handleSendLogin = async (p: Partner) => {
    if (!confirm(`發送「${p.name}」嘅登入連結？

連結（永久有效）會經 WhatsApp 發送到 Master 的登記電話；夥伴可長期重複點擊。`)) return;
    setSendingLogin(p.partner_id);
    try {
      const r = await sendPartnerLogin(p.partner_id);
      const first = r.sent?.[0];
      alert(first ? `✅ 已發送到 ${first.phone}（${r.count} 位 Master）

連結永久有效——對方開連結後即可登入夥伴後台與 Playbook。` : `✅ 已發送`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "發送失敗");
    } finally {
      setSendingLogin(null);
    }
  };

  // 2026-10-28：後台複製連結——取現行永久連結（冇有就簽一條），純剪貼板，唔發 WhatsApp
  const handleCopyLink = async (p: Partner) => {
    setCopyingLink(p.partner_id);
    try {
      const r = await getPartnerLink(p.partner_id);
      const link = String(r.link || "");
      if (!link) throw new Error("後台未回傳連結");
      await navigator.clipboard.writeText(link);
      alert(`✅ 已複製「${p.name}」的登入連結（永久有效），可直接交給夥伴：\n\n${link}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "複製失敗");
    } finally {
      setCopyingLink(null);
    }
  };

  // 2026-10-28：重設連結（洩露逃生門）——舊連結全部即時作廢＋簽新
  const handleResetLink = async (p: Partner) => {
    if (!confirm(`重設「${p.name}」的登入連結？

現行所有連結會即時失效（夥伴手上舊連結將打不開），並簽發一條新永久連結。適用於連結外洩或交接。`)) return;
    setResettingLink(p.partner_id);
    try {
      const r = await resetPartnerLink(p.partner_id);
      const link = String(r.link || "");
      if (link) await navigator.clipboard.writeText(link).catch(() => {});
      alert(`✅ 已重設「${p.name}」的登入連結（舊連結已全部作廢）${link ? "，新連結已複製：\n\n" + link : ""}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "重設失敗");
    } finally {
      setResettingLink(null);
    }
  };

  const handleDelete = async (p: Partner) => {
    if (!confirm(`確定刪除夥伴「${p.name}」（${p.partner_id}）？`)) return;
    try {
      await deletePartner(p.partner_id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "刪除失敗");
    }
  };

  const featureCheckboxes = (form: PartnerForm, setForm: (f: PartnerForm) => void, keyPrefix: string) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
      {featureDefs.map((def) => (
        <label key={`${keyPrefix}-${def.id}`} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            checked={form.features.includes(def.id)}
            onChange={() => toggleFeature(form, setForm, def.id)}
            className="w-3.5 h-3.5 accent-emerald-500"
          />
          <span>{def.name}{def.paid ? <span className="ml-1 text-amber-500">💰</span> : null}</span>
        </label>
      ))}
      {featureDefs.length === 0 && <p className="text-xs text-slate-500">（功能定義載入中…）</p>}
    </div>
  );

  const fieldCls = "w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-emerald-500";

  const renderFormFields = (form: PartnerForm, setForm: (f: PartnerForm) => void, keyPrefix: string) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-400 mb-1">網站</label>
          <input type="text" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://hkeid.com/" className={fieldCls} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">聯絡人</label>
            <input type="text" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="姓名" className={fieldCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">聯絡電話</label>
            <input type="text" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="852xxxxxxxx" className={fieldCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">服務號碼（渠道 AIBot 線）</label>
            <input type="text" value={form.wa_line} onChange={(e) => setForm({ ...form, wa_line: e.target.value })} placeholder="85269944777" className={fieldCls} />
          </div>
        </div>
      </div>
      <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-lg">
        <p className="text-xs font-semibold text-emerald-400 mb-1">AIBot 批發結算成本（非客人售價）</p>
        <p className="text-[11px] text-slate-500 mb-3">只用於計算「實付 − 批發成本 − 夥伴佣金」的淨額對賬，不會改變客人付款金額。客人實付以交易訂單為準，佣金按該訂單金額計算；其他品牌請到「多品牌銷售」管理 SKU 售價。</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Lite 6 個月批發成本（HK$）</label>
            <input type="number" step="0.01" min={0} value={form.price_lite_6mo} onChange={(e) => setForm({ ...form, price_lite_6mo: e.target.value })} placeholder="只供淨額對賬" className={fieldCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Lite 年繳批發成本（HK$）</label>
            <input type="number" step="0.01" min={0} value={form.price_lite_year} onChange={(e) => setForm({ ...form, price_lite_year: e.target.value })} placeholder="只供淨額對賬" className={fieldCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Pro 6 個月批發成本（HK$）</label>
            <input type="number" step="0.01" min={0} value={form.price_pro_6mo} onChange={(e) => setForm({ ...form, price_pro_6mo: e.target.value })} placeholder="只供淨額對賬" className={fieldCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Pro 年繳批發成本（HK$）</label>
            <input type="number" step="0.01" min={0} value={form.price_pro_year} onChange={(e) => setForm({ ...form, price_pro_year: e.target.value })} placeholder="只供淨額對賬" className={fieldCls} />
          </div>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-400 mb-1">費用安排備註</label>
        <textarea rows={2} value={form.billing_note} onChange={(e) => setForm({ ...form, billing_note: e.target.value })} placeholder="結算週期、出單方式等自由文字" className={fieldCls} />
      </div>
      <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-lg">
        <p className="text-xs font-semibold text-emerald-400 mb-2">🏷️ 白標配置（P3 Co-Branding 用）</p>
        <div className="space-y-2">
          <input type="text" value={form.co_displayName} onChange={(e) => setForm({ ...form, co_displayName: e.target.value })} placeholder="顯示名稱（例如 EiD）" className={fieldCls} />
          <input type="text" value={form.co_logoUrl} onChange={(e) => setForm({ ...form, co_logoUrl: e.target.value })} placeholder="Logo URL（https://…）" className={fieldCls} />
          <input type="text" value={form.co_portalTitle} onChange={(e) => setForm({ ...form, co_portalTitle: e.target.value })} placeholder="門戶標題（例如 EiD AI 助理）" className={fieldCls} />
        </div>
      </div>
      <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-lg">
        <p className="text-xs font-semibold text-emerald-400 mb-1">🧩 功能白名單（夥伴租戶預設開啟；未勾＝建租戶時明確關閉）</p>
        <p className="text-[11px] text-slate-500 mb-2">D5 凍結預設：核心對話＋多輪記憶＋智能分流＋轉真人。勾選改動只影響之後新建嘅租戶。</p>
        {featureCheckboxes(form, setForm, keyPrefix)}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-100">🤝 戰略夥伴</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setShowCreate((v) => !v); setEditingId(null); }}
            className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
          >
            {showCreate ? "收起表單" : "➕ 新增夥伴"}
          </button>
          <button onClick={load} className="px-4 py-2 text-sm bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors">
            🔄 重新載入
          </button>
        </div>
      </div>

      {keyReveal && <KeyReveal partnerId={keyReveal.partnerId} apiKey={keyReveal.apiKey} onClose={() => setKeyReveal(null)} />}

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
          <p className="text-sm text-rose-400">❌ {error}</p>
        </div>
      )}

      {showCreate && (
        <div className="mb-6 max-w-3xl bg-slate-900 rounded-xl border border-emerald-800/40 p-6">
          <h3 className="text-sm font-semibold text-emerald-400 mb-4">➕ 新增夥伴</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">夥伴 ID <span className="text-rose-400">*</span></label>
                <input type="text" value={newId} onChange={(e) => setNewId(e.target.value)} placeholder="例如 eid" className={`${fieldCls} font-mono`} />
                <p className="text-xs text-slate-500 mt-1">2-32 位小寫英數；建立後無法修改（API 歸因鍵）。</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1">夥伴名稱 <span className="text-rose-400">*</span></label>
                <input type="text" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="例如 EiD" className={fieldCls} />
              </div>
            </div>
            {renderFormFields(createForm, setCreateForm, "new")}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleCreate}
                disabled={creating}
                className="px-5 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors"
              >
                {creating ? "建立中…" : "➕ 建立夥伴（自動生成 API Key）"}
              </button>
              <p className="text-xs text-slate-500">建立後即刻生成 API Key——明文只顯示一次。</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">載入中...</div>
      ) : partners.length === 0 ? (
        <div className="p-8 bg-slate-900 rounded-xl border border-slate-800 text-center">
          <p className="text-sm text-slate-400">仲未有戰略夥伴——撳「➕ 新增夥伴」開始（首發夥伴：EiD）。</p>
        </div>
      ) : (
        <div className="space-y-4">
          {partners.map((p) => {
            const pTenants = tenantsByPartner[p.partner_id] || [];
            const isEditing = editingId === p.partner_id;
            const page = tenantPages[p.partner_id] ?? 0;
            const pageTenants = pTenants.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
            const pricing = parseJsonField<Record<string, number>>(p.wholesale_pricing, {});
            return (
              <div key={p.partner_id} className="max-w-4xl bg-slate-900 rounded-xl border border-slate-800 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-lg font-bold text-slate-100">{p.name}</h3>
                      <code className="px-2 py-0.5 rounded bg-slate-800 text-xs font-mono text-emerald-400">{p.partner_id}</code>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${p.status === "active" ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                        {p.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {p.website ? <a href={p.website} target="_blank" rel="noopener" className="text-sky-400 hover:underline">{p.website}</a> : "（未填網站）"}
                      {p.contact_name ? ` ・ ${p.contact_name}` : ""}
                      {p.contact_phone ? ` ・ ${p.contact_phone}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => { setEditingId(isEditing ? null : p.partner_id); setEditForm(formFromPartner(p)); setShowCreate(false); }}
                      className="px-3 py-1.5 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded transition-colors"
                    >
                      {isEditing ? "取消編輯" : "✏️ 編輯"}
                    </button>
                    <button
                      onClick={() => handleDelete(p)}
                      className="px-3 py-1.5 text-xs bg-rose-600/20 hover:bg-rose-600/30 text-rose-400 rounded transition-colors"
                    >
                      🗑️ 刪除
                    </button>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    {renderFormFields(editForm, setEditForm, `edit-${p.partner_id}`)}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleSave(p.partner_id)}
                        disabled={saving}
                        className="px-5 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors"
                      >
                        {saving ? "儲存中…" : "💾 儲存變更"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                      <div className="p-3 bg-slate-950/40 rounded-lg">
                        <p className="text-[11px] text-slate-500 mb-1">AIBot 批發結算成本（非客人售價）</p>
                        <p className="text-sm text-slate-200">
                          {(() => {
                            const entries = Object.entries(pricing);
                            if (entries.length === 0) return "（未填）";
                            return entries
                              .map(([k, v]) => `${PRICING_LABELS[k] || k} $${v}`)
                              .join(" ・ ");
                          })()}
                        </p>
                      </div>
                      <div className="p-3 bg-slate-950/40 rounded-lg">
                        <p className="text-[11px] text-slate-500 mb-1">名下租戶</p>
                        <p className="text-sm text-slate-200">{p.tenant_count ?? pTenants.length} 個</p>
                      </div>
                      <div className="p-3 bg-slate-950/40 rounded-lg">
                        <p className="text-[11px] text-slate-500 mb-1">功能白名單</p>
                        <p className="text-sm text-slate-200">{parseJsonField<string[]>(p.default_features, []).length} 項開啟</p>
                      </div>
                    </div>

                    <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-lg mb-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-xs font-semibold text-emerald-400">🔑 夥伴 API Key</p>
                          <p className="text-sm font-mono text-slate-300 mt-1">••••{(p.api_key_prefix || "————").slice(-4)}<span className="ml-2 text-[11px] font-sans text-slate-500">（明文只喺生成/重置時顯示一次）</span></p>
                        </div>
                        <button
                          onClick={() => handleRotate(p)}
                          className="px-3 py-1.5 text-xs bg-amber-700/40 hover:bg-amber-700/60 text-amber-200 rounded-lg transition-colors"
                        >
                          🔄 重新生成
                        </button>
                      </div>
                    </div>

                    <div className="p-3 bg-slate-950/40 border border-slate-700/50 rounded-lg mb-4">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-xs font-semibold text-emerald-400">📲 登入 Magic Link（永久有效）</p>
                          <p className="text-sm text-slate-300 mt-1">連結長期有效，可直接交給夥伴——夥伴後台＋Playbook 都用它解鎖<span className="ml-2 text-[11px] font-sans text-slate-500">（改聯絡電話＝身份自動跟隨；連結外洩時用「重設」即時作廢）</span></p>
                    <p className="text-xs text-slate-400 mt-2">🤖 服務號碼：<b className="text-slate-200">{p.wa_line || "未綁定 AIBot 線"}</b><span className="ml-2 text-[11px] font-sans text-slate-500">（經銷商自己嘅對外線——Agent 關鍵字偵測／渠道訊息發送；Playbook Magic Link 由此線回覆）</span></p>
                        </div>
                        <button
                          onClick={() => handleLoginAs(p)}
                          disabled={sendingLogin === p.partner_id}
                          className="px-3 py-1.5 text-xs bg-amber-500/10 border border-amber-400/40 text-amber-300 rounded-lg hover:bg-amber-500/20 disabled:opacity-40 whitespace-nowrap transition-colors"
                          title="以 Master 視角直接進入此夥伴的後台（不經 WhatsApp；已記錄審計）"
                        >
                          🕵️ 進入後台
                        </button>
                        <button
                          onClick={() => handleSendLogin(p)}
                          disabled={sendingLogin === p.partner_id}
                          className="px-3 py-1.5 text-xs bg-emerald-700/40 hover:bg-emerald-700/60 text-emerald-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {sendingLogin === p.partner_id ? "發送中…" : "📲 發送登入連結"}
                        </button>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleCopyLink(p)}
                            disabled={copyingLink === p.partner_id}
                            className="px-3 py-1.5 text-xs bg-sky-700/40 hover:bg-sky-700/60 text-sky-200 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {copyingLink === p.partner_id ? "取連結中…" : "📋 複製連結"}
                          </button>
                          <button
                            onClick={() => handleResetLink(p)}
                            disabled={resettingLink === p.partner_id}
                            className="px-3 py-1.5 text-xs bg-amber-700/40 hover:bg-amber-700/60 text-amber-200 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {resettingLink === p.partner_id ? "重設中…" : "🔄 重設連結"}
                          </button>
                          <button
                            onClick={() => handleSendLogin(p)}
                            disabled={sendingLogin === p.partner_id}
                            className="px-3 py-1.5 text-xs bg-emerald-700/40 hover:bg-emerald-700/60 text-emerald-200 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {sendingLogin === p.partner_id ? "發送中…" : "📲 發送登入連結"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {p.billing_note ? (
                      <p className="text-xs text-slate-400 mb-4">📝 {p.billing_note}</p>
                    ) : null}

                    {/* ── 旗下 Agent（2026-10-30 Captain：superadmin 看見並可逐個代入）── */}
                    <div className="mb-4">
                      <button
                        onClick={() => toggleAgents(p)}
                        className="text-xs text-amber-300 hover:text-amber-200 transition-colors"
                      >
                        {agentsOpen === p.partner_id ? "▲ 收起旗下 Agent" : `👥 查看旗下 Agent${agentsList[p.partner_id] ? `（${agentsList[p.partner_id].length}）` : ""}`}
                      </button>
                      {agentsOpen === p.partner_id && (
                        <div className="mt-3 overflow-x-auto">
                          {agentsLoading === p.partner_id ? (
                            <p className="text-xs text-slate-500">載入中…</p>
                          ) : (agentsList[p.partner_id] || []).length === 0 ? (
                            <p className="text-xs text-slate-500">尚未有 Agent 加入。</p>
                          ) : (
                            <table className="w-full text-sm text-left">
                              <thead className="text-xs text-slate-400 uppercase bg-slate-950 border-b border-slate-700">
                                <tr>
                                  <th className="px-3 py-2">Agent</th>
                                  <th className="px-3 py-2">電話</th>
                                  <th className="px-3 py-2">狀態</th>
                                  <th className="px-3 py-2">掛靠</th>
                                  <th className="px-3 py-2 text-center">小代理</th>
                                  <th className="px-3 py-2 text-center">點擊</th>
                                  <th className="px-3 py-2 text-center">成交</th>
                                  <th className="px-3 py-2">操作</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(agentsList[p.partner_id] || []).map((a) => (
                                  <tr key={a.code} className="border-b border-slate-800">
                                    <td className="px-3 py-2">
                                      <span className="text-slate-200 font-medium">{a.name || a.code}</span>
                                      <code className="ml-2 text-[10px] text-slate-500">{a.code}</code>
                                    </td>
                                    <td className="px-3 py-2 text-slate-400 text-xs">{a.phone}</td>
                                    <td className="px-3 py-2">
                                      <span className={a.status === "active" ? "text-emerald-400 text-xs" : "text-amber-400 text-xs"}>
                                        {a.status === "active" ? "已啟用" : "待審"}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-slate-400 text-xs">{a.parentName ? `↳ ${a.parentName}` : "直屬"}</td>
                                    <td className="px-3 py-2 text-center text-slate-300">{a.subs}</td>
                                    <td className="px-3 py-2 text-center text-slate-300">{a.clicks}</td>
                                    <td className="px-3 py-2 text-center text-slate-300">{a.converted}</td>
                                    <td className="px-3 py-2">
                                      <button
                                        onClick={() => handleLoginAsAgent(p, a)}
                                        disabled={sendingLogin === p.partner_id}
                                        className="px-2.5 py-1 text-xs bg-amber-500/10 border border-amber-400/40 text-amber-300 rounded-lg hover:bg-amber-500/20 disabled:opacity-40 whitespace-nowrap transition-colors"
                                        title="以此 Agent 視角直接進入夥伴後台（已記錄審計）"
                                      >
                                        🕵️ 代入
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() => setExpanded(expanded === p.partner_id ? null : p.partner_id)}
                        className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                      >
                        {expanded === p.partner_id ? "▲ 收起租戶清單" : `▼ 查看名下租戶（${pTenants.length}）`}
                      </button>
                      {expanded === p.partner_id && (
                        <div className="mt-3 overflow-x-auto">
                          {pTenants.length === 0 ? (
                            <p className="text-xs text-slate-500">名下暫時冇租戶。</p>
                          ) : (
                            <>
                              <table className="w-full text-sm text-left">
                                <thead className="text-xs text-slate-400 uppercase bg-slate-950 border-b border-slate-700">
                                  <tr>
                                    <th className="px-4 py-2">租戶 ID</th>
                                    <th className="px-4 py-2">Bot 名稱</th>
                                    <th className="px-4 py-2">定價層級</th>
                                    <th className="px-4 py-2">操作</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {pageTenants.map((t) => (
                                    <tr key={t.tenant_id} className="bg-slate-950/50 border-b border-slate-800 hover:bg-slate-800/50">
                                      <td className="px-4 py-2 font-mono text-emerald-400 text-xs">{t.tenant_id}</td>
                                      <td className="px-4 py-2">{t.bot_name || "—"}</td>
                                      <td className="px-4 py-2 text-xs">{t.plan_tier}</td>
                                      <td className="px-4 py-2">
                                        <button
                                          onClick={() => navigate(`/tenants/edit/${t.tenant_id}`)}
                                          className="px-3 py-1 text-xs bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 rounded transition-colors"
                                        >
                                          ✏️ 編輯
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <Pager
                                page={page}
                                totalPages={Math.max(1, Math.ceil(pTenants.length / PAGE_SIZE))}
                                total={pTenants.length}
                                onChange={(np) => setTenantPages((prev) => ({ ...prev, [p.partner_id]: np }))}
                              />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
